import { motion, useReducedMotion } from "framer-motion";

const ease = [0.22, 1, 0.36, 1];

const gold = "#A08448";

const MOTES = [
  { left: "12%", top: "22%", size: 3, duration: 18, delay: 0 },
  { left: "22%", top: "68%", size: 2, duration: 22, delay: 2.4 },
  { left: "38%", top: "16%", size: 2.5, duration: 20, delay: 1.1 },
  { left: "48%", top: "78%", size: 3, duration: 24, delay: 3.2 },
  { left: "63%", top: "28%", size: 2, duration: 19, delay: 0.8 },
  { left: "74%", top: "62%", size: 2.5, duration: 21, delay: 4.1 },
  { left: "86%", top: "20%", size: 3, duration: 23, delay: 1.7 },
  { left: "81%", top: "80%", size: 2, duration: 17, delay: 2.8 },
];

const Rule = () => (
  <svg viewBox="0 0 420 20" className="h-4 w-full max-w-md text-[#C4A56A]" fill="none" aria-hidden="true">
    <path d="M8 10 H188" stroke="currentColor" strokeWidth="0.75" />
    <path d="M232 10 H412" stroke="currentColor" strokeWidth="0.75" />
    <path d="M198 10c6-2.2 12-2.2 18 0" stroke="currentColor" strokeWidth="0.75" />
    <path d="M198 10c6 2.2 12 2.2 18 0" stroke="currentColor" strokeWidth="0.75" />
    <path d="M210 10l7-4.2 7 4.2-7 4.2-7-4.2Z" fill="currentColor" />
  </svg>
);

const SoftMotion = () => {
  const reduce = useReducedMotion();

  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true" data-testid="hero-motion">
      <motion.div
        className="absolute -left-[18%] -top-[20%] h-[70%] w-[70%] rounded-full bg-[radial-gradient(circle,rgba(192,150,90,0.22),transparent_64%)]"
        animate={reduce ? undefined : { x: [0, 36, -18, 0], y: [0, 22, -14, 0] }}
        transition={{ duration: 26, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="absolute -right-[16%] top-[8%] h-[62%] w-[58%] rounded-full bg-[radial-gradient(circle,rgba(160,132,72,0.16),transparent_66%)]"
        animate={reduce ? undefined : { x: [0, -28, 16, 0], y: [0, 18, -10, 0] }}
        transition={{ duration: 30, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="absolute bottom-[-24%] left-[18%] h-[58%] w-[64%] rounded-full bg-[radial-gradient(circle,rgba(247,241,229,0.9),transparent_70%)]"
        animate={reduce ? undefined : { x: [0, 20, -12, 0], y: [0, -16, 10, 0] }}
        transition={{ duration: 28, repeat: Infinity, ease: "easeInOut" }}
      />
      {!reduce &&
        MOTES.map((mote, i) => (
          <motion.span
            key={i}
            className="absolute rounded-full bg-[#C4A36A]"
            style={{
              left: mote.left,
              top: mote.top,
              width: mote.size,
              height: mote.size,
              opacity: 0.35,
            }}
            animate={{ y: [0, -18, 8, 0], opacity: [0.12, 0.45, 0.2, 0.12] }}
            transition={{ duration: mote.duration, delay: mote.delay, repeat: Infinity, ease: "easeInOut" }}
          />
        ))}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(247,241,229,0.72)_0%,rgba(247,241,229,0.28)_48%,transparent_74%)]" />
    </div>
  );
};

export const Hero = () => {
  const reduce = useReducedMotion();

  return (
    <section
      id="top"
      className="relative overflow-hidden bg-[#F7F1E5] px-5 pb-14 pt-24 text-[#1C140E] sm:px-6 sm:pb-20 sm:pt-28 md:px-10 md:pb-24 md:pt-32"
      data-testid="hero-section"
    >
      <SoftMotion />
      <motion.img
        src="/assets/hero-floral-left.png?v=2"
        alt=""
        animate={reduce ? undefined : { y: [0, -10, 6, 0], scale: [1, 1.03, 1] }}
        transition={{ duration: 32, repeat: Infinity, ease: "easeInOut" }}
        className="pointer-events-none absolute bottom-0 left-0 top-0 hidden h-full w-auto select-none md:block"
      />
      <motion.img
        src="/assets/hero-floral-right.png?v=2"
        alt=""
        animate={reduce ? undefined : { y: [0, 8, -8, 0], scale: [1, 1.025, 1] }}
        transition={{ duration: 34, repeat: Infinity, ease: "easeInOut" }}
        className="pointer-events-none absolute bottom-0 right-0 top-0 hidden h-full w-auto select-none md:block"
      />

      <div className="relative z-[1] mx-auto flex max-w-3xl flex-col items-center px-1 text-center sm:px-4">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease }}
          className="flex flex-col items-center"
          data-testid="hero-hotel-mark"
        >
          <img src="/assets/hero-crest.png?v=2" alt="" className="h-[4.85rem] w-auto sm:h-32" />
          <p className="mt-2.5 font-serif text-[11px] font-medium uppercase tracking-[0.42em] sm:mt-3 sm:text-xs" style={{ color: gold }}>
            Community Hearts
          </p>
          <p
            className="mt-2.5 flex items-center gap-3 text-[10px] font-medium uppercase tracking-[0.46em]"
            style={{ color: gold }}
            data-testid="hero-kicker"
          >
            <span className="h-px w-8 sm:w-12" style={{ backgroundColor: gold }} aria-hidden="true" />
            Private
            <span className="h-px w-8 sm:w-12" style={{ backgroundColor: gold }} aria-hidden="true" />
          </p>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.12, ease }}
          className="mt-5 font-serif text-[2.15rem] font-medium leading-[1.08] tracking-tight text-[#1A120C] sm:mt-8 sm:text-6xl sm:leading-[1.02] md:mt-10 md:text-[4.6rem]"
          data-testid="hero-headline"
        >
          A quiet place
          <br />
          is being held.
        </motion.h1>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7, delay: 0.24, ease }}
          className="mt-4 w-full max-w-md sm:mt-6"
        >
          <Rule />
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.32, ease }}
          className="mt-4 font-serif text-[1.4rem] italic leading-none sm:mt-5 sm:text-3xl"
          style={{ color: gold }}
          data-testid="hero-subhead"
        >
          Starting in Richmond.
        </motion.p>

        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.42, ease }}
          className="mt-4 font-serif text-[15px] leading-relaxed text-[#2A2118] sm:mt-5 sm:text-lg"
        >
          Introductions arranged with care.
          <br />
          Five days to mean it. No games.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.52, ease }}
          className="mt-6 sm:mt-8"
        >
          <a
            href="/waitlist.html"
            data-testid="hero-get-app-button"
            className="inline-flex items-center gap-4 rounded-full bg-[#C0965A] px-9 py-3.5 text-[11px] font-semibold uppercase tracking-[0.22em] text-[#FFF8E8] transition-colors duration-300 hover:bg-[#B0894C] sm:px-11"
          >
            Request an invitation
            <span aria-hidden="true" className="text-sm font-normal leading-none">
              ›
            </span>
          </a>
        </motion.div>
      </div>
    </section>
  );
};
